import React, { useEffect, useState } from "react";

type ToolkitPage = "home" | "useful-state";

const REFLECTION_KEY = "sanctuary:toolkit:useful-state-reflection";

type Reflection = {
  noticed: string;
  useful: string;
  evidence: string;
  nextStep: string;
};

const emptyReflection: Reflection = {
  noticed: "",
  useful: "",
  evidence: "",
  nextStep: "",
};

type ToolCardProps = {
  number: string;
  title: string;
  introduction: string;
  children: React.ReactNode;
};

function ToolCard({ number, title, introduction, children }: ToolCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <article className={`tool-card${open ? " tool-card--open" : ""}`}>
      <button
        type="button"
        className="tool-card-heading"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="tool-number">{number}</span>
        <span className="tool-heading-copy">
          <strong>{title}</strong>
          <small>{introduction}</small>
        </span>
        <span className="tool-card-action">{open ? "Close" : "Explore"}</span>
      </button>
      {open && <div className="tool-card-body">{children}</div>}
    </article>
  );
}

export default function ToolkitView() {
  const [page, setPage] = useState<ToolkitPage>("home");
  const [reflection, setReflection] = useState<Reflection>(emptyReflection);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(REFLECTION_KEY);
      if (stored) setReflection({ ...emptyReflection, ...JSON.parse(stored) });
    } catch {
      // A reflection can still be used without browser storage.
    }
  }, []);

  function updateReflection(field: keyof Reflection, value: string) {
    setReflection((current) => ({ ...current, [field]: value }));
    setSaved(false);
  }

  function saveReflection() {
    try {
      localStorage.setItem(REFLECTION_KEY, JSON.stringify(reflection));
      setSaved(true);
    } catch {
      setSaved(false);
    }
  }

  if (page === "home") {
    return (
      <section className="toolkit-shell">
        <div className="toolkit-hero">
          <p className="toolkit-eyebrow">Your personal collection of practical tools</p>
          <h1>Your Toolkit</h1>
          <p className="toolkit-lead">
            The Toolkit is not here to tell you who to become. It helps you discover,
            develop and combine practical tools that support you in becoming your
            “Best Me”—bringing out more of the capability already within you.
          </p>
        </div>

        <div className="toolkit-guidance">
          <h2>How to use your Toolkit</h2>
          <p>
            There is no single right tool and no compulsory order. Try an approach,
            notice what happens and keep or adapt what is useful to you. Some sections
            contain one complete tool; others show how several tools can work together.
          </p>
        </div>

        <div className="toolkit-section-grid">
          <button
            type="button"
            className="toolkit-section-card toolkit-section-card--available"
            onClick={() => setPage("useful-state")}
          >
            <span className="toolkit-card-status">A useful place to begin</span>
            <span className="toolkit-card-number">01</span>
            <strong>Getting into a Useful Frame of Mind</strong>
            <span>
              Explore four different ways to create more calm, clarity or space for
              the next thing you want to do.
            </span>
            <b>Open this section →</b>
          </button>
        </div>

        <p className="toolkit-growing-note">
          Your Toolkit will grow over time. New sections will be added as they are
          developed, tested and ready to earn their place.
        </p>
      </section>
    );
  }

  return (
    <section className="toolkit-shell">
      <button type="button" className="toolkit-back" onClick={() => setPage("home")}>
        ← Back to your Toolkit
      </button>

      <div className="toolkit-hero toolkit-hero--section">
        <p className="toolkit-eyebrow">Toolkit section 01</p>
        <h1>Getting into a Useful Frame of Mind</h1>
        <p className="toolkit-lead">
          Different moments call for different states. These tools offer several ways
          to create enough calm, clarity or imagination to take your next step. You do
          not need to like—or use—all of them.
        </p>
      </div>

      <div className="tool-list">
        <ToolCard
          number="01"
          title="Creating a Useful State"
          introduction="Relax, notice the change and create a simple reminder you can reuse."
        >
          <h3>Try it gently</h3>
          <ol>
            <li>Choose a comfortable place where you are unlikely to be interrupted.</li>
            <li>Let your breathing settle and release tension from your shoulders and jaw.</li>
            <li>Remember a time when you felt calm, capable or quietly confident.</li>
            <li>Notice what you could see, hear and feel as that useful state returns.</li>
            <li>At its strongest point, make a small, discreet gesture—such as touching a finger and thumb together.</li>
            <li>Pause, then repeat the gesture later and notice whether it helps you reconnect with that state.</li>
          </ol>
          <p>
            Treat this as an experiment, not a test. The gesture is simply a personal
            reminder, and it may become more useful through calm repetition.
          </p>
        </ToolCard>

        <ToolCard
          number="02"
          title="Box Breathing"
          introduction="Use an even breathing rhythm to slow things down and create space."
        >
          <h3>A simple round</h3>
          <ol>
            <li>Breathe in gently for a count of four.</li>
            <li>Pause for a count of four.</li>
            <li>Breathe out gently for a count of four.</li>
            <li>Pause for a count of four, then repeat for a few comfortable rounds.</li>
          </ol>
          <p>
            Four is a guide, not a target. Shorten the count or remove the pauses if
            that feels better. Stop if you feel uncomfortable or light-headed.
          </p>
        </ToolCard>

        <ToolCard
          number="03"
          title="Contrasting Breathing"
          introduction="Explore how a more energising breathing pattern feels compared with a settling one."
        >
          <div className="tool-safety-note">
            <strong>Safety first</strong>
            <p>
              Only practise seated or lying down in a safe place. Never use breath
              retention in water, while driving or anywhere a faint could cause harm.
              Stop immediately if you feel unwell. If you have a relevant health
              condition, are pregnant or are unsure whether this is suitable, seek
              professional medical advice before trying it.
            </p>
          </div>
          <h3>Notice the contrast</h3>
          <p>
            Begin with a few comfortable, slightly fuller breaths, without forcing the
            pace or depth. Return to ordinary breathing and notice the difference in
            energy, focus and physical sensation. The purpose here is observation—not
            endurance, extreme breath-holding or pushing through discomfort.
          </p>
        </ToolCard>

        <ToolCard
          number="04"
          title="Learn to Daydream Effectively"
          introduction="Give your imagination a direction without demanding an immediate answer."
        >
          <h3>Give your mind useful space</h3>
          <ol>
            <li>Choose one question, possibility or situation to explore.</li>
            <li>Move somewhere safe where your mind can wander—a chair, garden or gentle walk.</li>
            <li>Imagine possibilities without judging or organising them too quickly.</li>
            <li>Notice ideas, images or connections that appear naturally.</li>
            <li>When you finish, capture anything useful in a few words.</li>
          </ol>
          <p>
            Effective daydreaming is not avoiding reality. It creates space to see
            possibilities before deciding what, if anything, deserves action.
          </p>
        </ToolCard>
      </div>

      <section className="tool-combination">
        <p className="toolkit-eyebrow">Tools can work together</p>
        <h2>One possible combination</h2>
        <p>
          Use a breathing exercise to settle or energise yourself, allow ideas to
          emerge through effective daydreaming, then record what you noticed. This is
          only an example—you choose the combination that is useful for you.
        </p>
      </section>

      <section className="tool-reflection">
        <p className="toolkit-eyebrow">Pause and recognise what happened</p>
        <h2>What I Noticed</h2>
        <p>
          This is not a checklist and there are no expected answers. It is a gentle
          place to notice what you tried, what changed and the evidence that you have
          already taken a step forward.
        </p>

        <label>
          What did I notice while trying these approaches?
          <textarea
            value={reflection.noticed}
            onChange={(event) => updateReflection("noticed", event.target.value)}
          />
        </label>
        <label>
          Which part felt useful—or might become useful with practice or adaptation?
          <textarea
            value={reflection.useful}
            onChange={(event) => updateReflection("useful", event.target.value)}
          />
        </label>
        <label>
          My evidence of progress
          <span>Trying, noticing, adapting or deciding something is not for you can all be evidence.</span>
          <textarea
            value={reflection.evidence}
            onChange={(event) => updateReflection("evidence", event.target.value)}
          />
        </label>
        <label>
          Is there one small, real situation where I would like to use a tool next?
          <textarea
            value={reflection.nextStep}
            onChange={(event) => updateReflection("nextStep", event.target.value)}
          />
        </label>

        <div className="tool-reflection-actions">
          <button type="button" onClick={saveReflection}>Save what I noticed</button>
          {saved && <p role="status">Saved. This is evidence that you stopped, noticed and took a step forward.</p>}
        </div>
      </section>
    </section>
  );
}
